/**
 * Каталог Я.Маркет — описание колонок таблицы и словарь статусов карточки.
 *
 * По образцу `features/ozon/catalog/columns.tsx`, но набор колонок и статусы —
 * свои: у Маркета `cardStatus` из 9 значений вместо строки состояния Ozon, и
 * нет остатков по складам/скидок (Этап 1 — только чтение зеркала каталога).
 */

import React from 'react'
import { Badge, type BadgeVariant } from '../../../components/ui/Badge'
import { marketplaceThumbnailUrl } from '../../../shared/media/marketplaceThumbnails'
import type { CatalogColumn } from '../../marketplace-catalog'
import { YmPhotoThumb } from './photoStatus'
import type { YmCardStatus, YmCatalogProduct } from './types'

/**
 * Словарь статусов карточки Маркета — все 9 значений Партнёрского API
 * (SPEC_2026-09-13_YANDEX_MARKET.md §3.4). Неизвестное значение — код как
 * есть серым, без падения: справочник статусов площадка может расширить.
 */
export const YM_STATUS_INFO: Record<YmCardStatus, { label: string; tone: BadgeVariant }> = {
  HAS_CARD_CAN_NOT_UPDATE: { label: 'На витрине, контент Маркета', tone: 'blue' },
  HAS_CARD_CAN_UPDATE: { label: 'На витрине', tone: 'green' },
  HAS_CARD_CAN_UPDATE_PROCESSING: { label: 'Правка на модерации', tone: 'yellow' },
  HAS_CARD_CAN_UPDATE_ERRORS: { label: 'Правка отклонена', tone: 'red' },
  NO_CARD_NEED_CONTENT: { label: 'Нужен контент', tone: 'yellow' },
  NO_CARD_PROCESSING: { label: 'На модерации', tone: 'yellow' },
  NO_CARD_ERRORS: { label: 'Отклонена', tone: 'red' },
  NO_CARD_MARKET_WILL_CREATE: { label: 'Маркет создаст карточку', tone: 'blue' },
  NO_CARD_ADD_TO_CAMPAIGN: { label: 'Добавить в магазин', tone: 'gray' },
}

export const ymStatusLabel = (status?: string | null): string => {
  if (!status) return 'Без статуса'
  return YM_STATUS_INFO[status as YmCardStatus]?.label || status
}

export const ymStatusVariant = (status?: string | null): BadgeVariant =>
  (status && YM_STATUS_INFO[status as YmCardStatus]?.tone) || 'gray'

/** Статусы вкладки «На витрине» — карточка уже создана площадкой. */
export const YM_SHOWCASE_STATUSES: YmCardStatus[] = ['HAS_CARD_CAN_NOT_UPDATE', 'HAS_CARD_CAN_UPDATE']
/** Статусы вкладки «На модерации» — ждём решения площадки. */
export const YM_MODERATION_STATUSES: YmCardStatus[] = ['NO_CARD_PROCESSING', 'HAS_CARD_CAN_UPDATE_PROCESSING']
/** Статусы вкладки «Нужен контент» — карточки без контента в разных состояниях. */
export const YM_NEED_CONTENT_STATUSES: YmCardStatus[] = [
  'NO_CARD_NEED_CONTENT',
  'NO_CARD_MARKET_WILL_CREATE',
  'NO_CARD_ADD_TO_CAMPAIGN',
]
/** Статусы, которые сама площадка называет отклонением/ошибкой карточки. */
export const YM_ERROR_STATUSES: YmCardStatus[] = ['HAS_CARD_CAN_UPDATE_ERRORS', 'NO_CARD_ERRORS']

export const rowKeyOf = (product: YmCatalogProduct): string => String(product.id ?? product.offer_id ?? '')

export const photoUrlOf = (product: YmCatalogProduct): string =>
  product.main_picture || (Array.isArray(product.pictures) ? product.pictures[0] : '') || ''

export const formatMoney = (value?: number | null, currency?: string | null): string => {
  if (value === null || value === undefined) return '—'
  const number = Number(value)
  if (!Number.isFinite(number)) return '—'
  // Единственная известная валюта площадки для наших кабинетов — рубль;
  // символ подставляем только для RUB/пусто, иначе печатаем код как есть.
  const suffix = !currency || currency === 'RUR' || currency === 'RUB' ? '₽' : currency
  return `${Math.round(number).toLocaleString('ru-RU')} ${suffix}`
}

export const formatDateTime = (value?: string | null): string =>
  value
    ? new Date(value).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

const dash = <span className="text-gray-300 dark:text-zinc-600">—</span>

const Thumb: React.FC<{ product: YmCatalogProduct }> = ({ product }) => {
  const url = photoUrlOf(product)
  return (
    <YmPhotoThumb
      src={url ? marketplaceThumbnailUrl(url, 250) : null}
      uploadState={product.main_picture_state}
      alt={product.name || ''}
      zoom
    />
  )
}

export const YM_COLUMNS: Array<CatalogColumn<YmCatalogProduct>> = [
  {
    key: 'product',
    label: 'Товар',
    width: 340,
    minWidth: 250,
    sortColumn: 'name',
    locked: true,
    render: (product, { mode }) => (
      <span className="flex min-w-0 items-center gap-2.5">
        {mode === 'standard' && <Thumb product={product} />}
        <span className="min-w-0 flex-1">
          <span
            className="block truncate text-[0.8125rem] text-gray-900 dark:text-zinc-100"
            title={product.name || undefined}
          >
            {product.name || 'Без названия'}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5">
            <span className="shrink-0 font-mono text-xs text-gray-500 dark:text-zinc-400">
              {product.offer_id || '—'}
            </span>
            {product.vendor && (
              <span className="truncate text-xs text-gray-500 dark:text-zinc-400">
                · {product.vendor}
              </span>
            )}
          </span>
        </span>
      </span>
    ),
  },
  {
    key: 'offerId',
    label: 'Артикул',
    width: 150,
    minWidth: 110,
    sortColumn: 'offer_id',
    render: (product) => (
      <span className="block truncate font-mono text-[0.8125rem]">{product.offer_id || '—'}</span>
    ),
  },
  {
    key: 'category',
    label: 'Категория Маркета',
    width: 190,
    minWidth: 140,
    render: (product) =>
      product.market_category_name ? (
        <span className="block truncate text-gray-700 dark:text-zinc-300" title={product.market_category_name}>
          {product.market_category_name}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'status',
    label: 'Статус',
    width: 190,
    minWidth: 140,
    sortColumn: 'card_status',
    align: 'center',
    render: (product) => (
      <Badge variant={ymStatusVariant(product.card_status)} dot>
        {ymStatusLabel(product.card_status)}
      </Badge>
    ),
  },
  {
    key: 'marketSku',
    label: 'Market SKU',
    width: 130,
    minWidth: 105,
    render: (product) =>
      product.market_sku ? (
        <span className="block truncate font-mono text-xs">{product.market_sku}</span>
      ) : (
        dash
      ),
  },
  {
    key: 'contentRating',
    label: 'Рейтинг контента',
    width: 130,
    minWidth: 105,
    align: 'center',
    render: (product) =>
      product.content_rating !== null && product.content_rating !== undefined ? (
        <span className="tabular-nums">{product.content_rating}%</span>
      ) : (
        dash
      ),
  },
  {
    key: 'price',
    label: 'Цена кабинета',
    width: 130,
    minWidth: 105,
    sortColumn: 'basic_price',
    align: 'right',
    render: (product) => (
      <span className="block font-semibold tabular-nums">
        {formatMoney(product.basic_price, product.currency)}
      </span>
    ),
  },
  {
    key: 'pim',
    label: 'PIM',
    width: 130,
    minWidth: 100,
    align: 'center',
    render: (product) =>
      product.pim_article ? (
        <span className="block truncate font-mono text-xs text-indigo-700 dark:text-indigo-300">
          {product.pim_article}
        </span>
      ) : (
        dash
      ),
  },
  {
    key: 'errors',
    label: 'Ошибки',
    width: 95,
    minWidth: 80,
    align: 'center',
    render: (product) => (product.has_errors ? <Badge variant="red">есть</Badge> : dash),
  },
  {
    key: 'archived',
    label: 'Архив',
    width: 90,
    minWidth: 75,
    align: 'center',
    render: (product) => (product.archived ? <Badge variant="gray">в архиве</Badge> : dash),
  },
  {
    key: 'updatedAt',
    label: 'Обновлено',
    width: 145,
    minWidth: 120,
    sortColumn: 'updated_at',
    render: (product) => (
      <span className="block whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400">
        {formatDateTime(product.updatedAt)}
      </span>
    ),
  },
  {
    key: 'lastSync',
    label: 'Синхронизировано',
    width: 150,
    minWidth: 125,
    render: (product) => (
      <span className="block whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400">
        {formatDateTime(product.last_sync_at)}
      </span>
    ),
  },
]

/** Колонки, видимые до того как пользователь настроит своё */
export const YM_DEFAULT_VISIBLE = [
  'product',
  'category',
  'status',
  'marketSku',
  'price',
  'pim',
  'updatedAt',
]

/** Колонки выгрузки в XLS */
export const YM_XLS_COLUMNS = [
  { key: 'offer_id', header: 'Артикул', value: (p: YmCatalogProduct) => p.offer_id || '' },
  { key: 'name', header: 'Название', value: (p: YmCatalogProduct) => p.name || '' },
  { key: 'category', header: 'Категория Маркета', value: (p: YmCatalogProduct) => p.market_category_name || '' },
  { key: 'status', header: 'Статус', value: (p: YmCatalogProduct) => ymStatusLabel(p.card_status) },
  { key: 'market_sku', header: 'Market SKU', value: (p: YmCatalogProduct) => p.market_sku ?? '' },
  { key: 'content_rating', header: 'Рейтинг контента, %', value: (p: YmCatalogProduct) => p.content_rating ?? '' },
  { key: 'price', header: 'Цена кабинета', value: (p: YmCatalogProduct) => p.basic_price ?? '' },
  { key: 'pim', header: 'Артикул PIM', value: (p: YmCatalogProduct) => p.pim_article || '' },
  { key: 'has_errors', header: 'Есть ошибки', value: (p: YmCatalogProduct) => (p.has_errors ? 'да' : 'нет') },
  { key: 'archived', header: 'В архиве', value: (p: YmCatalogProduct) => (p.archived ? 'да' : 'нет') },
  { key: 'updated_at', header: 'Обновлено', value: (p: YmCatalogProduct) => formatDateTime(p.updatedAt) },
]
